import type {
  AccountQualityEnum,
  AccountFragment,
} from "src/graphql/generated/types";

interface CoownerAccountUnit {
  unit: string;
  saleDate?: Date | string | null;
}

interface UnitWithId {
  id: string;
}

interface AccountWithUnitCount {
  __typename?: string | null;
  count: {
    units: number;
  };
  building?: {
    units: readonly UnitWithId[];
  };
}

function getSaleDateTimestamp(
  saleDate: CoownerAccountUnit["saleDate"],
): number {
  if (saleDate === null || saleDate === undefined) {
    return Number.POSITIVE_INFINITY;
  }

  const timestamp = new Date(saleDate).getTime();

  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
}

export function normalizeCoownerAccountUnits(
  units: readonly CoownerAccountUnit[],
): CoownerAccountUnit[] {
  const unitsById = new Map<string, CoownerAccountUnit>();

  for (const unit of units) {
    const currentUnit = unitsById.get(unit.unit);

    if (
      !currentUnit ||
      getSaleDateTimestamp(unit.saleDate) >
        getSaleDateTimestamp(currentUnit.saleDate)
    ) {
      unitsById.set(unit.unit, unit);
    }
  }

  return Array.from(unitsById.values());
}

export function getCurrentlyOwnedUnitIds(
  units: readonly CoownerAccountUnit[],
  now = new Date(),
): Set<string> {
  const nowTimestamp = now.getTime();

  return new Set(
    normalizeCoownerAccountUnits(units)
      .filter(({ saleDate }) => getSaleDateTimestamp(saleDate) > nowTimestamp)
      .map(({ unit }) => unit),
  );
}

export function getUniqueUnitsById<T extends UnitWithId>(
  units: readonly T[],
): T[] {
  const unitsById = new Map<string, T>();

  for (const unit of units) {
    if (!unitsById.has(unit.id)) {
      unitsById.set(unit.id, unit);
    }
  }

  return Array.from(unitsById.values());
}

export function getAccountUnitCount(account: AccountWithUnitCount): number {
  if (account.__typename === "CoownerAccount" && account.building) {
    return getUniqueUnitsById(account.building.units).length;
  }

  return account.count.units;
}

export function isTrusteeCouncilMember(account: AccountFragment): boolean {
  return (
    account.__typename === "CoownerAccount" && account.isTrusteeCouncilMember
  );
}

export function decodeBase64UUID(encodedUUID: string): {
  quality: AccountQualityEnum;
  customerId: string;
  accountId: string;
} {
  const decodedString = atob(encodedUUID);

  const decodedObject = JSON.parse(decodedString);

  return decodedObject;
}
